// src/Pages/Admin/Referrals/accessGrantDialogs.js
import React, { useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TableContainer,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Alert,
  CircularProgress,
  InputAdornment,
  Tabs,
  Tab,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Collapse,
  Badge
} from '@mui/material';
import {
  Delete,
  Search,
  CheckCircle,
  Clear,
  AttachMoney,
  Lock,
  LockOpen,
  Share,
  Info,
  Business,
  Person,
  Public,
  Edit,
  Settings,
  Save,
  ExpandMore,
  ExpandLess,
  Add,
  Close,
  Category,
  People
} from '@mui/icons-material';
import client from '../../../../../../API';
import { toast } from 'sonner';
import { COMMISSION_TYPES, TypeRateConfigPanel } from './commisiontypeconfig';

// =================== RATE BREAKDOWN VIEWER DIALOG ===================
export const RateBreakdownDialog = ({ open, onClose, accessId, grantInfo }) => {
  const { data: breakdown, isLoading } = useQuery(
    ['accessRateBreakdown', accessId],
    () => client.commissions.getAccessRateBreakdown(accessId),
    { enabled: open && !!accessId }
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Category color="primary" />
            <Typography variant="h6">Commission Rate Breakdown</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {grantInfo && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>{grantInfo.global_user?.display_name || grantInfo.global_user?.username}</strong>
              {' • '}Base Rate: <strong>{grantInfo.commission_percentage}%</strong>
              {' • '}Term: <strong>{grantInfo.term_months} months</strong>
            </Typography>
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : breakdown?.rate_breakdown ? (
          <TypeRateConfigPanel
            commissionTypes={COMMISSION_TYPES}
            baseRate={breakdown.base_rate_bps}
            typeSpecificRates={breakdown.rate_breakdown.reduce((acc, item) => {
              if (item.has_override) {
                acc[item.commission_type] = item.rate_bps;
              }
              return acc;
            }, {})}
            enabledTypes={breakdown.rate_breakdown
              .filter(item => item.is_enabled)
              .map(item => item.commission_type)
            }
            readOnly={true}
          />
        ) : (
          <Alert severity="warning">Unable to load rate breakdown</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// =================== EDIT ACCESS GRANT DIALOG (MULTI-TYPE) ===================
export const EditAccessGrantDialog = ({ open, onClose, grant, onSuccess }) => {
  const [commissionBps, setCommissionBps] = useState(grant?.commission_percentage_bps || 25);
  const [termMonths, setTermMonths] = useState(grant?.term_months || 12);
  const [isActive, setIsActive] = useState(grant?.is_active ?? true);
  const [notes, setNotes] = useState(grant?.notes || '');
  const [typeSpecificRates, setTypeSpecificRates] = useState(grant?.type_specific_rates || {});
  const [enabledTypes, setEnabledTypes] = useState(grant?.enabled_commission_types || null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (grant) {
      setCommissionBps(grant.commission_percentage_bps);
      setTermMonths(grant.term_months);
      setIsActive(grant.is_active);
      setNotes(grant.notes || '');
      setTypeSpecificRates(grant.type_specific_rates || {});
      setEnabledTypes(grant.enabled_commission_types || null);
    }
  }, [grant]);

  const updateAccessMutation = useMutation(
    (data) => client.commissions.updateAccessGrant(grant.id, data),
    {
      onSuccess: () => {
        toast.success('Access grant updated successfully');
        queryClient.invalidateQueries(['referralAccessGrants', grant.referral_id]);
        queryClient.invalidateQueries(['accessRateBreakdown', grant.id]);
        queryClient.invalidateQueries('referralsAndCommissions');
        queryClient.invalidateQueries('allAccessGrants');
        onSuccess?.();
        onClose();
      },
      onError: (error) => {
        toast.error(error?.response?.data?.error || 'Failed to update access grant');
      }
    }
  );

  const handleSave = () => {
    updateAccessMutation.mutate({
      commissionPercentageBps: commissionBps,
      typeSpecificRates,
      enabledCommissionTypes: enabledTypes,
      termMonths,
      isActive,
      notes: notes || undefined
    });
  };

  const handleUpdateTypeRate = (typeId, rate) => {
    setTypeSpecificRates(prev => ({
      ...prev,
      [typeId]: rate
    }));
  };

  const handleDeleteTypeRate = (typeId) => {
    setTypeSpecificRates(prev => {
      const updated = { ...prev };
      delete updated[typeId];
      return updated;
    });
  };

  const handleToggleType = (typeId) => {
    if (enabledTypes === null) {
      setEnabledTypes(COMMISSION_TYPES.map(t => t.id).filter(id => id !== typeId));
    } else {
      if (enabledTypes.includes(typeId)) {
        const updated = enabledTypes.filter(id => id !== typeId);
        setEnabledTypes(updated.length === COMMISSION_TYPES.length ? null : updated);
      } else {
        const updated = [...enabledTypes, typeId];
        setEnabledTypes(updated.length === COMMISSION_TYPES.length ? null : updated);
      }
    }
  };

  const commissionPercentage = (commissionBps / 100).toFixed(2);
  const activeOverrides = Object.keys(typeSpecificRates).length;
  const enabledCount = enabledTypes === null ? COMMISSION_TYPES.length : enabledTypes.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Edit color="primary" />
            <Typography variant="h6">Edit Access Grant</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {grant && (
          <Box sx={{ mt: 2 }}>
            {/* User Info Card */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
              <Box display="flex" alignItems="center" gap={2}>
                {grant.global_user?.type === 'creator' ? (
                  <Person sx={{ color: 'primary.main', fontSize: 32 }} />
                ) : (
                  <Business sx={{ color: 'secondary.main', fontSize: 32 }} />
                )}
                <Box flex={1}>
                  <Typography variant="body1" fontWeight="medium">
                    {grant.global_user?.display_name || grant.global_user?.username}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {grant.global_user?.email}
                  </Typography>
                </Box>
                {grant.scope_type === 'all' && (
                  <Chip icon={<Public />} label="All-Access" color="primary" />
                )}
              </Box>
            </Paper>

            {/* Base Configuration */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Base Commission Rate (Basis Points)"
                  type="number"
                  value={commissionBps}
                  onChange={(e) =>
                    setCommissionBps(
                      Math.max(0, Math.min(10000, parseInt(e.target.value) || 0))
                    )
                  }
                  helperText={`${commissionPercentage}% - Applied to all enabled types unless overridden`}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">bps</InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Term (Months)"
                  type="number"
                  value={termMonths}
                  onChange={(e) =>
                    setTermMonths(Math.max(1, parseInt(e.target.value) || 12))
                  }
                  helperText="Duration of commission rights"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">months</InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Status</FormLabel>
                  <RadioGroup
                    row
                    value={isActive ? 'active' : 'inactive'}
                    onChange={(e) => setIsActive(e.target.value === 'active')}
                  >
                    <FormControlLabel 
                      value="active" 
                      control={<Radio />} 
                      label={
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <LockOpen fontSize="small" />
                          <span>Active</span>
                        </Box>
                      }
                    />
                    <FormControlLabel 
                      value="inactive" 
                      control={<Radio />} 
                      label={
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Lock fontSize="small" />
                          <span>Inactive</span>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>

            {/* Advanced Multi-Type Configuration */}
            <Card variant="outlined" sx={{ mb: 2 }}>
              <Box
                sx={{ 
                  p: 2, 
                  bgcolor: 'grey.50', 
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Category color="primary" />
                  <Typography variant="subtitle1" fontWeight="medium">
                    Type-Specific Commission Configuration
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Badge badgeContent={enabledCount} color="success">
                      <Chip label="Enabled" size="small" variant="outlined" />
                    </Badge>
                    {activeOverrides > 0 && (
                      <Badge badgeContent={activeOverrides} color="warning">
                        <Chip label="Overrides" size="small" variant="outlined" />
                      </Badge>
                    )}
                  </Stack>
                </Box>
                <IconButton size="small">
                  {showAdvanced ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              
              <Collapse in={showAdvanced}>
                <Box sx={{ p: 2 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      Configure commission rates per revenue type. Override rates take precedence over the base rate.
                      Disable individual types to prevent commission on specific revenue streams.
                    </Typography>
                  </Alert>

                  <TypeRateConfigPanel
                    commissionTypes={COMMISSION_TYPES}
                    baseRate={commissionBps}
                    typeSpecificRates={typeSpecificRates}
                    enabledTypes={enabledTypes}
                    onUpdateTypeRate={handleUpdateTypeRate}
                    onDeleteTypeRate={handleDeleteTypeRate}
                    onToggleType={handleToggleType}
                  />

                  {/* Active Overrides Summary */}
                  {activeOverrides > 0 && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.50', borderRadius: 1 }}>
                      <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
                        Active Custom Rates ({activeOverrides})
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {Object.entries(typeSpecificRates).map(([typeId, rate]) => {
                          const typeConfig = COMMISSION_TYPES.find(t => t.id === typeId);
                          return (
                            <Chip
                              key={typeId}
                              avatar={<Avatar sx={{ bgcolor: `${typeConfig?.color}.light` }}>{typeConfig?.icon}</Avatar>}
                              label={`${typeConfig?.label}: ${(rate / 100).toFixed(2)}%`}
                              size="small"
                              color={typeConfig?.color}
                              onDelete={() => handleDeleteTypeRate(typeId)}
                              sx={{ mb: 1 }}
                            />
                          );
                        })}
                      </Stack>
                    </Box>
                  )}
                </Box>
              </Collapse>
            </Card>

            {/* Notes */}
            <TextField
              fullWidth
              label="Notes (Optional)"
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this access grant..."
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={updateAccessMutation.isLoading}
          startIcon={updateAccessMutation.isLoading ? <CircularProgress size={20} /> : <Save />}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// =================== ACCESS GRANTS TABLE DIALOG ===================
export const AccessGrantsDialog = ({ open, onClose, referralId }) => {
  const [selectedGrant, setSelectedGrant] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [breakdownDialogOpen, setBreakdownDialogOpen] = useState(false);

  const queryClient = useQueryClient();

  const { data: accessData, isLoading } = useQuery(
    ['referralAccessGrants', referralId],
    () => client.commissions.getReferralAccessGrants(referralId),
    { enabled: open && !!referralId }
  );

  const deleteAccessMutation = useMutation(
    (accessId) => client.commissions.deleteAccessGrant(accessId),
    {
      onSuccess: () => {
        toast.success('Access grant deleted successfully');
        queryClient.invalidateQueries(['referralAccessGrants', referralId]);
        queryClient.invalidateQueries('referralsAndCommissions');
      },
      onError: (error) => {
        toast.error(error?.response?.data?.error || 'Failed to delete access grant');
      }
    }
  );

  const handleEdit = (grant) => {
    setSelectedGrant(grant);
    setEditDialogOpen(true);
  };

  const handleViewBreakdown = (grant) => {
    setSelectedGrant(grant);
    setBreakdownDialogOpen(true);
  };

  const handleDelete = (accessId) => {
    if (window.confirm('Are you sure you want to revoke this access grant?')) {
      deleteAccessMutation.mutate(accessId);
    }
  };

  const getActiveOverridesCount = (grant) => {
    return Object.keys(grant.type_specific_rates || {}).length;
  };

  const getEnabledTypesCount = (grant) => {
    if (!grant.enabled_commission_types) return COMMISSION_TYPES.length;
    return grant.enabled_commission_types.length;
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <Settings color="primary" />
              <Typography variant="h6">Access Grants</Typography>
            </Box>
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {isLoading ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : accessData?.access_grants?.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Base Commission</TableCell>
                    <TableCell>Configuration</TableCell>
                    <TableCell>Term</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accessData.access_grants.map((grant) => {
                    const overridesCount = getActiveOverridesCount(grant);
                    const enabledCount = getEnabledTypesCount(grant);
                    const hasCustomConfig = overridesCount > 0 || enabledCount !== COMMISSION_TYPES.length;
                    
                    return (
                      <TableRow key={grant.id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {grant.global_user?.type === 'creator' ? (
                              <Person sx={{ fontSize: 20, color: 'primary.main' }} />
                            ) : (
                              <Business sx={{ fontSize: 20, color: 'secondary.main' }} />
                            )}
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {grant.global_user?.display_name || grant.global_user?.username}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {grant.global_user?.email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {grant.commission_percentage}%
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            ({grant.commission_percentage_bps} bps)
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Chip 
                                label={`${enabledCount}/${COMMISSION_TYPES.length} types`} 
                                size="small" 
                                color={enabledCount === COMMISSION_TYPES.length ? "success" : "warning"}
                                variant="outlined"
                              />
                              {hasCustomConfig && (
                                <Tooltip title="View rate breakdown">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleViewBreakdown(grant)}
                                    color="info"
                                  >
                                    <Info fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                            {overridesCount > 0 && (
                              <Chip 
                                label={`${overridesCount} custom rate${overridesCount !== 1 ? 's' : ''}`} 
                                size="small" 
                                color="warning"
                              />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={`${grant.term_months}mo`} 
                            size="small" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {grant.is_active ? (
                            <Chip icon={<LockOpen fontSize="small" />} label="Active" size="small" color="success" />
                          ) : (
                            <Chip icon={<Lock fontSize="small" />} label="Inactive" size="small" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {new Date(grant.created_at).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="Edit Configuration">
                              <IconButton size="small" color="primary" onClick={() => handleEdit(grant)}>
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Grant">
                              <IconButton 
                                size="small" 
                                color="error" 
                                onClick={() => handleDelete(grant.id)}
                                disabled={deleteAccessMutation.isLoading}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">No access grants for this referral</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <EditAccessGrantDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedGrant(null);
        }}
        grant={selectedGrant}
        onSuccess={() => {
          queryClient.invalidateQueries(['referralAccessGrants', referralId]);
          queryClient.invalidateQueries('referralsAndCommissions');
        }}
      />

      <RateBreakdownDialog
        open={breakdownDialogOpen}
        onClose={() => {
          setBreakdownDialogOpen(false);
          setSelectedGrant(null);
        }}
        accessId={selectedGrant?.id}
        grantInfo={selectedGrant}
      />
    </>
  );
};

// =================== CREATE PARTNER ACCESS DIALOG ===================
export const CreatePartnerAccessDialog = ({ open, onClose, referrals = [], onSuccess }) => {
  const [step, setStep] = useState(1);
  const [accessType, setAccessType] = useState('specific');
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [commissionBps, setCommissionBps] = useState(25);
  const [termMonths, setTermMonths] = useState(12);
  const [notes, setNotes] = useState('');
  const [referralSearch, setReferralSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [typeSpecificRates, setTypeSpecificRates] = useState({});
  const [enabledTypes, setEnabledTypes] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (!open) {
      setStep(1);
      setAccessType('specific');
      setSelectedReferral(null);
      setSelectedUser(null);
      setCommissionBps(25);
      setTermMonths(12);
      setNotes('');
      setReferralSearch('');
      setUserSearch('');
      setTypeSpecificRates({});
      setEnabledTypes(null);
      setShowAdvanced(false);
    }
  }, [open]);

  const filteredReferrals = useMemo(() => {
    if (!referralSearch) return referrals;
    const q = referralSearch.toLowerCase();
    return referrals.filter((r) =>
      [r.first_name, r.last_name, r.email, r.referral_type, r.status]
        .filter(Boolean)
        .some((v) => v.toString().toLowerCase().includes(q))
    );
  }, [referrals, referralSearch]);

  const { data: searchResults, isLoading: isSearching } = useQuery(
    ['users-search-access', userSearch],
    async () => {
      if (!userSearch || userSearch.length < 2) return [];
      const response = await client.referrals.searchUsers(userSearch);
      return response.users || [];
    },
    {
      enabled: userSearch.length >= 2,
      staleTime: 30000,
      keepPreviousData: true
    }
  );

  const grantAccessMutation = useMutation(
    (data) => client.commissions.createAccessGrant(data),
    {
      onSuccess: () => {
        toast.success('Access granted successfully');
        queryClient.invalidateQueries('referralsAndCommissions');
        queryClient.invalidateQueries('allAccessGrants');
        onSuccess?.();
        onClose();
      },
      onError: (error) => {
        toast.error(error?.response?.data?.error || 'Failed to grant access');
      }
    }
  );

  const handleGrantAccess = () => {
    if (!selectedUser) {
      toast.error('Please select a user');
      return;
    }

    if (accessType === 'specific' && !selectedReferral) {
      toast.error('Please select a referral');
      return;
    }

    const payload = {
      globalUserId: selectedUser.id,
      scopeType: accessType === 'all' ? 'all' : 'referral',
      commissionPercentageBps: commissionBps,
      termMonths,
      notes: notes || undefined
    };

    if (accessType === 'specific') {
      payload.referralId = selectedReferral.id;
    }

    if (Object.keys(typeSpecificRates).length > 0) {
      payload.typeSpecificRates = typeSpecificRates;
    }

    if (enabledTypes !== null) {
      payload.enabledCommissionTypes = enabledTypes;
    }

    grantAccessMutation.mutate(payload);
  };

  const handleNext = () => {
    if (step === 1) {
      if (accessType === 'all') {
        setStep(3);
      } else {
        setStep(2);
      }
    } else if (step === 2) {
      if (!selectedReferral) {
        toast.error('Please select a referral');
        return;
      }
      setStep(3);
    } else if (step === 3) {
      if (!selectedUser) {
        toast.error('Please select a user');
        return;
      }
      setStep(4);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
    } else if (step === 3) {
      if (accessType === 'all') {
        setStep(1);
      } else {
        setStep(2);
      }
    } else if (step === 4) {
      setStep(3);
    }
  };

  const commissionPercentage = (commissionBps / 100).toFixed(2);
  const activeOverrides = Object.keys(typeSpecificRates).length;
  const enabledCount = enabledTypes === null ? COMMISSION_TYPES.length : enabledTypes.length;

  const getTabValue = () => {
    if (step === 1) return 0;
    if (step === 2) return 1;
    if (step === 3) return accessType === 'all' ? 1 : 2;
    if (step === 4) return accessType === 'all' ? 2 : 3;
    return 0;
  };

  const renderAccessTypeStep = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        Choose whether to grant access to a specific referral or all referrals
      </Alert>
      <FormControl component="fieldset">
        <FormLabel component="legend">Access Type</FormLabel>
        <RadioGroup
          value={accessType}
          onChange={(e) => setAccessType(e.target.value)}
        >
          <FormControlLabel
            value="specific"
            control={<Radio />}
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Specific Referral
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Grant access to commission from one specific referral
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            value="all"
            control={<Radio />}
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  <Public sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                  All Referrals
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Grant access to commission from ALL current and future referrals
                </Typography>
              </Box>
            }
          />
        </RadioGroup>
      </FormControl>
    </Box>
  );

  const renderReferralSelectionStep = () => (
    <Box>
      <TextField
        fullWidth
        placeholder="Search referrals by name, email, type, status..."
        value={referralSearch}
        onChange={(e) => setReferralSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          )
        }}
        sx={{ mb: 2 }}
      />
      <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
        {filteredReferrals.length ? (
          filteredReferrals.map((r) => (
            <Paper
              key={r.id}
              sx={{
                p: 2,
                mb: 1,
                cursor: 'pointer',
                border: selectedReferral?.id === r.id ? 2 : 1,
                borderColor:
                  selectedReferral?.id === r.id ? 'primary.main' : 'divider',
                '&:hover': { bgcolor: 'action.hover' }
              }}
              onClick={() => setSelectedReferral(r)}
            >
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: 'primary.light' }}>
                  {(r.first_name?.[0] || '').toUpperCase()}
                  {(r.last_name?.[0] || '').toUpperCase()}
                </Avatar>
                <Box flex={1}>
                  <Typography variant="body1" fontWeight="medium">
                    {r.first_name} {r.last_name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {r.email}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={r.referral_type}
                  color={r.referral_type === 'company' ? 'primary' : 'secondary'}
                  sx={{ mr: 1 }}
                />
                <Chip
                  size="small"
                  label={r.status}
                  variant="outlined"
                />
              </Box>
            </Paper>
          ))
        ) : (
          <Alert severity="info">No referrals found.</Alert>
        )}
      </Box>
    </Box>
  );

  const renderUserSelectionStep = () => (
    <Box>
      {accessType === 'all' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>All-Access Grant:</strong> This user will receive commission on{' '}
            <strong>ALL</strong> referrals (current and future)
          </Typography>
        </Alert>
      )}
      {accessType === 'specific' && selectedReferral && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Linking access to:&nbsp;
            <strong>
              {selectedReferral.first_name} {selectedReferral.last_name}
            </strong>{' '}
            ({selectedReferral.email})
          </Typography>
        </Alert>
      )}

      <TextField
        fullWidth
        placeholder="Search users by name, username, or email..."
        value={userSearch}
        onChange={(e) => setUserSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {isSearching ? <CircularProgress size={20} /> : <Search />}
            </InputAdornment>
          )
        }}
        sx={{ mb: 2 }}
      />

      {searchResults && searchResults.length > 0 ? (
        <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
          {searchResults.map((user) => (
            <Paper
              key={user.id}
              sx={{
                p: 2,
                mb: 1,
                cursor: 'pointer',
                border: selectedUser?.id === user.id ? 2 : 1,
                borderColor:
                  selectedUser?.id === user.id ? 'primary.main' : 'divider',
                '&:hover': { bgcolor: 'action.hover' }
              }}
              onClick={() => setSelectedUser(user)}
            >
              <Box display="flex" alignItems="center" gap={2}>
                {user.type === 'creator' ? (
                  <Person sx={{ color: 'primary.main' }} />
                ) : (
                  <Business sx={{ color: 'secondary.main' }} />
                )}
                <Box flex={1}>
                  <Typography variant="body1" fontWeight="medium">
                    {user.display_name || user.username}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {user.email}
                  </Typography>
                </Box>
                <Chip
                  label={user.type}
                  size="small"
                  color={user.type === 'creator' ? 'primary' : 'secondary'}
                />
              </Box>
            </Paper>
          ))}
        </Box>
      ) : userSearch.length >= 2 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="textSecondary">
            No users found matching "{userSearch}"
          </Typography>
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <People sx={{ fontSize: 48, color: 'action.disabled', mb: 2 }} />
          <Typography color="textSecondary">
            Start typing to search for users
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderConfigurationStep = () => (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2}>
          {selectedUser.type === 'creator' ? (
            <Person sx={{ color: 'primary.main' }} />
          ) : (
            <Business sx={{ color: 'secondary.main' }} />
          )}
          <Box flex={1}>
            <Typography variant="body1" fontWeight="medium">
              {selectedUser.display_name || selectedUser.username}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {selectedUser.email}
            </Typography>
          </Box>
          <Button size="small" onClick={() => setStep(3)}>
            Change User
          </Button>
        </Box>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Base Commission Rate (Basis Points)"
            type="number"
            value={commissionBps}
            onChange={(e) =>
              setCommissionBps(
                Math.max(0, Math.min(10000, parseInt(e.target.value) || 0))
              )
            }
            helperText={`${commissionPercentage}% - Applied to all enabled types unless overridden`}
            InputProps={{
              endAdornment: <InputAdornment position="end">bps</InputAdornment>
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Term (Months)"
            type="number"
            value={termMonths}
            onChange={(e) =>
              setTermMonths(Math.max(1, parseInt(e.target.value) || 12))
            }
            helperText="Duration of commission rights"
            InputProps={{
              endAdornment: <InputAdornment position="end">months</InputAdornment>
            }}
          />
        </Grid>
      </Grid>

      {/* Multi-Type Configuration */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <Box
          sx={{ 
            p: 2, 
            bgcolor: 'grey.50', 
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            '&:hover': { bgcolor: 'grey.100' }
          }}
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Category color="primary" />
            <Typography variant="subtitle1" fontWeight="medium">
              Type-Specific Configuration (Optional)
            </Typography>
            <Stack direction="row" spacing={1}>
              <Badge badgeContent={enabledCount} color="success">
                <Chip label="Enabled" size="small" variant="outlined" />
              </Badge>
              {activeOverrides > 0 && (
                <Badge badgeContent={activeOverrides} color="warning">
                  <Chip label="Overrides" size="small" variant="outlined" />
                </Badge>
              )}
            </Stack>
          </Box>
          <IconButton size="small">
            {showAdvanced ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
        
        <Collapse in={showAdvanced}>
          <Box sx={{ p: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                By default, all commission types are enabled at the base rate. 
                Customize rates per type or disable specific types as needed.
              </Typography>
            </Alert>

            <TypeRateConfigPanel
              commissionTypes={COMMISSION_TYPES}
              baseRate={commissionBps}
              typeSpecificRates={typeSpecificRates}
              enabledTypes={enabledTypes}
              onUpdateTypeRate={(typeId, rate) => 
                setTypeSpecificRates(prev => ({ ...prev, [typeId]: rate }))
              }
              onDeleteTypeRate={(typeId) =>
                setTypeSpecificRates(prev => {
                  const updated = { ...prev };
                  delete updated[typeId];
                  return updated;
                })
              }
              onToggleType={(typeId) => {
                if (enabledTypes === null) {
                  setEnabledTypes(COMMISSION_TYPES.map(t => t.id).filter(id => id !== typeId));
                } else {
                  if (enabledTypes.includes(typeId)) {
                    const updated = enabledTypes.filter(id => id !== typeId);
                    setEnabledTypes(updated.length === COMMISSION_TYPES.length ? null : updated);
                  } else {
                    const updated = [...enabledTypes, typeId];
                    setEnabledTypes(updated.length === COMMISSION_TYPES.length ? null : updated);
                  }
                }
              }}
            />
          </Box>
        </Collapse>
      </Card>

      <TextField
        fullWidth
        label="Notes (Optional)"
        multiline
        rows={3}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Add any notes about this access grant..."
        sx={{ mb: 2 }}
      />

      <Alert severity="success">
        <Typography variant="body2">
          <strong>{selectedUser.display_name || selectedUser.username}</strong>{' '}
          will receive commission on{' '}
          {accessType === 'all' ? (
            <>
              <strong>ALL referrals</strong> (current and future)
            </>
          ) : (
            <>
              invoices from{' '}
              <strong>
                {selectedReferral?.first_name} {selectedReferral?.last_name}
              </strong>
            </>
          )}
          {' '}for <strong>{termMonths} months</strong>.
          {activeOverrides > 0 && (
            <> With <strong>{activeOverrides} custom rate{activeOverrides !== 1 ? 's' : ''}</strong>.</>
          )}
          {enabledCount !== COMMISSION_TYPES.length && (
            <> <strong>{enabledCount} of {COMMISSION_TYPES.length}</strong> types enabled.</>
          )}
        </Typography>
      </Alert>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Share color="primary" />
          <Typography variant="h6">Create Partner Access</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Tabs value={getTabValue()} sx={{ mb: 3 }}>
          <Tab label="Access Type" />
          {accessType === 'specific' && <Tab label="Select Referral" disabled={step < 2} />}
          <Tab label="Select User" disabled={step < 3} />
          <Tab label="Configure Access" disabled={step < 4} />
        </Tabs>

        {step === 1 && renderAccessTypeStep()}
        {step === 2 && renderReferralSelectionStep()}
        {step === 3 && renderUserSelectionStep()}
        {step === 4 && renderConfigurationStep()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {step > 1 && (
          <Button onClick={handleBack} disabled={grantAccessMutation.isLoading}>
            Back
          </Button>
        )}
        {step < 4 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={
              (step === 2 && !selectedReferral) ||
              (step === 3 && !selectedUser)
            }
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleGrantAccess}
            disabled={grantAccessMutation.isLoading}
            startIcon={grantAccessMutation.isLoading ? <CircularProgress size={20} /> : <Share />}
          >
            Grant Access
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};